import Shared from '../../../Templates/Core/JS/Shared.js';

document.addEventListener('DOMContentLoaded', () => {
	class MainMenuTracking {
		/**
		 * Get performance duration
		 * @param {string} action
		 * @returns {number}
		 * static
		 */
		static getActionInteractionDuration(action) {
			performance.mark(action);
			performance.measure(action + '_ga_measure', 'page_load_start_mark');
			return Math.round(performance.getEntriesByName(action + '_ga_measure')[0].duration);
		}

		/**
		 * Pushes tracking event into Google Analytics & Redshift
		 * @param {string} action
		 * @param {string} label
		 * static
		 */
		static trackingHandler(action = '', label = '') {
			const category = 'HeaderMainMenu';
			const duration = this.getActionInteractionDuration(action);

			Shared.addFunctionToPipelineTracker((dataTracker) => {
				dataTracker.trackStructEvent(category, action, label, duration);
			});
		}
	}

	const HeaderMainMenu = {
		/**
		 * Important DOM elements
		 */
		elements: {
			showMenuButton: null,
			hideMenuButton: null,
			searchRowWrapper: null,
			headerMainMenu: null,
			sectionNavigationItems: [],
			sectionsContent: null,
			background: null
		},

		scrollBarWidth: 0,

		previouslyOpenedMenuItem: null,

		/**
		 * Initializes the header main menu
		 */
		init() {
			this.setMenuElements();
			this.addEventListeners();
			this.setScrollBarWidth();
		},

		/**
		 * Set needed elements for the header navigation
		 */
		setMenuElements() {
			this.elements.showMenuButton = document.querySelector('.js-showMainMenuButton');
			this.elements.hideMenuButton = document.querySelector('.js-hideMainMenuButton');
			this.elements.searchRowWrapper = document.getElementById('SearchRowWrapper');

			const headerMainMenu = document.getElementById('HeaderMainMenu');
			this.elements.headerMainMenu = headerMainMenu;

			this.elements.sectionNavigationItems = Array.from(headerMainMenu.querySelectorAll('.SectionNavigationItem'));
			this.elements.sectionsContent = headerMainMenu.querySelector('.SectionsContent');
			this.elements.background = document.getElementById('MainMenuBackground');
		},

		/**
		 * Add all event listeners to header navigation
		 */
		addEventListeners() {
			this.addShowMenuListener();
			this.addHideMenuListener();
			this.addSectionNavigationItemListeners();
			this.addSectionOptionsItemListeners();
			this.addBackgroundListener();
			this.addEscapeKeyListener();
			this.addNavBarOtherButtonsListeners();

			const windowResizeListener = Shared.debounce(() => {
				this.repositionSectionsContent();
			}, 100);

			window.addEventListener('resize', windowResizeListener);
		},

		/**
		 *  Initialize show menu button click to show main menu
		 */
		addShowMenuListener() {
			this.elements.showMenuButton.addEventListener('click', () => {
				this.openMainMenu();

				// Prevent iOS devices from scrolling the background instead of the main menu content
				document.querySelectorAll('.SectionContent').forEach((element) => {
					window.Loot.BodyScrollLock.disableBodyScroll(element);
				});

				// Track menu show click
				MainMenuTracking.trackingHandler(`header_menu_clicked_show_menu`, {
					mobile_menu_state: 'open'
				});
			});
		},

		/**
		 *  Initialize open menu button click to hide main menu
		 */
		addHideMenuListener() {
			this.elements.hideMenuButton.addEventListener('click', () => {
				this.closeMainMenu();

				document.querySelectorAll('.SectionContent').forEach((element) => {
					window.Loot.BodyScrollLock.enableBodyScroll(element);
				});

				MainMenuTracking.trackingHandler(`header_menu_clicked_hide_menu`, {
					mobile_menu_state: 'closed'
				});
			});
		},

		/**
		 * Open the main menu
		 */
		openMainMenu() {
			const openedSectionsItems = this.getOpenedSectionsItem();

			if (!openedSectionsItems) {
				let openItem;

				// on mobile, reopen last item
				if (Shared.breakpoints(['Small', 'Medium']) && this.previouslyOpenedMenuItem) {
					openItem = this.previouslyOpenedMenuItem;
				} else {
					openItem = this.elements.sectionNavigationItems
						.filter((item) => {
							return item.parentNode.classList.contains('SectionsNavigation');
						})
						.reverse()
						.pop();
				}

				openItem.querySelector('.SectionNavigationLink').click();
			}

			// Add class to body to be able to add the menu background layer
			document.body.classList.add('js-HeaderMainMenuOpened');
		},

		/**
		 * Add click listeners to every section navigation item
		 */
		addSectionNavigationItemListeners() {
			this.elements.sectionNavigationItems.forEach((sectionNavigationItem) => {
				sectionNavigationItem.addEventListener('click', (item) => {
					const itemSection = item.target.parentNode.dataset.menuSection;
					if (!this.allowClosingMenuItems(itemSection)) {
						return;
					}

					if (item.target.classList.contains('SectionNavigationLink')) {
						this.setMainMenuState(itemSection);
						const section = item.target.dataset.menuTrackingNavLink;
						const event = item.target.dataset.menuTrackingEvent;

						MainMenuTracking.trackingHandler(`header_menu_clicked_section_${section}_${event}`);
					}
				});
			});
		},

		/**
		 * Add click listeners to every section option
		 */
		addSectionOptionsItemListeners() {
			Array.from(this.elements.sectionsContent.querySelectorAll('.SectionContent')).forEach((SectionContent) => {
				SectionContent.addEventListener('click', (item) => {
					if (!item.target.closest('a')) {
						return;
					}

					const labels = {
						header_section: SectionContent.dataset.menuTrackingSection,
						header_sub_section: item.target.dataset.menuTrackingLink
					};

					MainMenuTracking.trackingHandler(
						`header_menu_clicked_subsection`,
						HeaderMainMenu.concatObjectPropertiesAsString(labels)
					);

					// Automatically close the menu after click a content link
					this.closeMainMenu();
				});
			});
		},

		/**
		 * Add listener to clicking the background layer
		 */
		addBackgroundListener() {
			this.elements.background.addEventListener('click', () => {
				this.closeMainMenu();

				MainMenuTracking.trackingHandler(`header_menu_clicked_menu_background`, {
					mobile_menu_state: 'closed'
				});
			});
		},

		/**
		 * Add listener to escape key
		 */
		addEscapeKeyListener() {
			const _this = this;
			document.onkeydown = function (evt) {
				evt = evt || window.event;

				if (evt.key !== 'Escape') {
					return;
				}

				if (_this.mainMenuIsOpened()) {
					_this.closeMainMenu();

					MainMenuTracking.trackingHandler(`header_menu_pressed_escape`, {
						mobile_menu_state: 'closed'
					});
				}
			};
		},

		/**
		 * Add click listeners on buttons within the NavBar to close the menu when its opened
		 */
		addNavBarOtherButtonsListeners() {
			const HeaderLogin = document.getElementById('HeaderLogin');

			HeaderLogin.addEventListener('click', (item) => {
				if (!this.mainMenuIsOpened()) {
					return;
				}

				// Close the menu when clicking on other links within nav bar (e.g. wish list / login)
				if (item.target.closest('a') || item.target.closest('button')) {
					this.closeMainMenu();
				}
			});
		},

		/**
		 * Set state for both navigational items and content
		 * @param {string} itemSection
		 */
		setMainMenuState(itemSection) {
			this.setSectionsNavigationState(itemSection);
			this.setSectionsContentState();
			this.repositionSectionsContent();
		},

		/**
		 * Set state for navigational items
		 * @param {string} itemSection
		 */
		setSectionsNavigationState(itemSection) {
			// set section navigation items into correct state
			this.elements.sectionNavigationItems.forEach((navigationItem) => {
				if (itemSection === navigationItem.dataset.menuSection || navigationItem.classList.contains('is-open')) {
					navigationItem.classList.toggle('is-open');
				}
			});
		},

		/**
		 * Check if it is allowed set the main menu to having no items opened
		 * @param {string} itemSection
		 * @returns {boolean}
		 */
		allowClosingMenuItems(itemSection) {
			// prevent mobile from closing a menu item
			const openedSection = this.getOpenedSectionsItem();
			if (this.isBreakpointMobile() && openedSection) {
				if (openedSection.navigation.dataset.menuSection === itemSection) {
					return false;
				}
			}

			return true;
		},

		/**
		 * Get navigational HTML elements for the opened section
		 * @returns {{navigation: *, content: *}}
		 */
		getOpenedSectionsItem() {
			const sectionsNavigationItems = this.elements.sectionNavigationItems.filter((item) => {
				return item.classList.contains('is-open');
			});

			if (sectionsNavigationItems.length === 0) {
				return;
			}

			return {
				navigation: sectionsNavigationItems.find((item) => item.parentNode.classList.contains('SectionsNavigation')),
				content: sectionsNavigationItems.find((item) => item.parentNode.classList.contains('SectionsContent'))
			};
		},

		/**
		 * Set state for sections content
		 */
		setSectionsContentState() {
			const activeItem = this.elements.sectionNavigationItems.find((navigationItem) => {
				return navigationItem.classList.contains('is-open');
			});

			// set sections content into correct state
			if (activeItem) {
				this.elements.sectionsContent.classList.add('is-open');
				document.body.classList.add('js-HeaderMainMenuOpened');
			} else {
				this.elements.sectionsContent.classList.remove('is-open');
				document.body.classList.remove('js-HeaderMainMenuOpened');
			}

			this.setScrollBarOffset();
		},

		/**
		 * Check is header main menu is being opened
		 * @returns {boolean}
		 */
		mainMenuIsOpened() {
			return (
				typeof this.elements.sectionNavigationItems.find((item) => item.classList.contains('is-open')) !== 'undefined'
			);
		},

		/**
		 * Close main menu
		 */
		closeMainMenu() {
			document.body.classList.remove('js-HeaderMainMenuOpened');

			let transitionTimer = 0;
			if (Shared.breakpoints(['Small', 'Medium'])) {
				transitionTimer = 500;
				this.previouslyOpenedMenuItem = this.getOpenedSectionsItem().navigation;
			} else {
				this.previouslyOpenedMenuItem = null;
			}

			setTimeout(() => {
				this.elements.sectionNavigationItems.forEach((item) => {
					item.classList.remove('is-open');
				});
				this.setSectionsContentState();
			}, transitionTimer);
		},

		/**
		 * Repositions drop down during resizing window
		 */
		repositionSectionsContent() {
			const openedSectionsItems = this.getOpenedSectionsItem();
			if (openedSectionsItems) {
				const offset = this.repositionNavigationItems(openedSectionsItems);
				this.repositionSectionContent(openedSectionsItems, offset);
			}
		},

		/**
		 * Repositions navigation items based on breakpoint
		 * @param {Object} items
		 * @returns {Number} offset
		 */
		repositionNavigationItems(items) {
			let offset = items.navigation.offsetLeft;

			if (this.isBreakpointMobile()) {
				items.content.style.left = offset + 'px';
			} else if (this.isBreakpointExtraLarge()) {
				offset -= 174;
				items.content.style.left = offset + 'px';
			} else {
				items.content.querySelector('.SectionNavigationLink').style.left = offset + 'px';
			}
			return offset;
		},

		/**
		 * Repositions drop down content based on breakpoint
		 * @param {Object} items
		 * @param {Number} offset
		 */
		repositionSectionContent(items, offset) {
			const activeSectionContent = items.content.querySelector('.SectionContent');

			let sectionRepositioning = 0;
			if (this.isBreakpointExtraLarge()) {
				sectionRepositioning = this.stickToNavigationRepositioning(activeSectionContent);
			} else if (this.isBreakpointLarge()) {
				sectionRepositioning = this.stickToCenterRepositioning(activeSectionContent);
			} else if (this.isBreakpointMobile()) {
				sectionRepositioning = -offset;
			}
			activeSectionContent.setAttribute('style', `left: ${sectionRepositioning}px`);
		},

		/**
		 * Reposition offset when section content needs to stick to navigation
		 * @param {HTMLElement} activeSectionContent
		 * @returns {number}
		 */
		stickToNavigationRepositioning(activeSectionContent) {
			const sectionContentRight = activeSectionContent.getBoundingClientRect().right + 24;
			const sectionContentDifference = sectionContentRight - window.innerWidth;

			if (activeSectionContent.offsetLeft - sectionContentDifference < 0) {
				return -Math.abs(activeSectionContent.offsetLeft - sectionContentDifference);
			}
			return 0;
		},

		/**
		 * Reposition offset when section content needs to stick to center of window
		 * @param {HTMLElement} activeSectionContent
		 * @returns {number}
		 */
		stickToCenterRepositioning(activeSectionContent) {
			return (window.innerWidth - activeSectionContent.offsetWidth - 32) / 2;
		},

		/**
		 * Get the labels as a piped string
		 * @param {Object} object
		 */
		concatObjectPropertiesAsString(object) {
			const objectProperties = [];

			for (let property in object) {
				if (Object.prototype.hasOwnProperty.call(object, property)) {
					objectProperties.push(property + ':' + object[property]);
				}
			}
			return objectProperties.join('|');
		},

		/**
		 * Checks if current breakpoint is Small or Medium
		 * @returns {boolean}
		 */
		isBreakpointMobile() {
			return Shared.breakpoints(['Small', 'Medium']);
		},

		/**
		 * Checks if current breakpoint is Large
		 * @returns {boolean}
		 */
		isBreakpointLarge() {
			return Shared.breakpoints(['Large']);
		},

		/**
		 * Checks if current breakpoint is ExtraLarge
		 * @returns {boolean}
		 */
		isBreakpointExtraLarge() {
			return Shared.breakpoints(['ExtraLarge']);
		},

		/**
		 * Determine the width of the scrollbar (browser dependent)
		 */
		setScrollBarWidth() {
			this.scrollBarWidth = window.innerWidth - document.documentElement.offsetWidth;
		},

		/**
		 * Apply scrollbar offset correction (for fixed header navigation)
		 */
		setScrollBarOffset() {
			let scrollBarOffset = 0;
			if (document.documentElement.offsetWidth === window.innerWidth) {
				scrollBarOffset = this.scrollBarWidth;
			}
			this.elements.searchRowWrapper.style.paddingRight = scrollBarOffset + 'px';
		}
	};

	HeaderMainMenu.init();
});
